import { nanoid } from 'nanoid';
import { supabase } from "@/lib/supabaseClient";
import {
  QuizGame,
  QuizQuestion,
  QuizUserProgress,
  QuizLeaderboardEntry,
  FlowUserPathRecord,
  FlowGameResults
} from "@/models/quiz-game";
import {
  QuizGameCreateInput,
  QuizGameUpdateInput,
  QuizQuestionCreateInput,
  QuizQuestionUpdateInput,
  QuizOption
} from "@/models/quiz-game/types";
import { getCacheOrFetch, setCache } from "@/utils/cacheService";

// CRUD cho Quiz Games
export async function getGameById(id: string): Promise<QuizGame | null> {
  const cacheKey = `quiz_game_${id}`;
  
  return getCacheOrFetch(cacheKey, async () => {
    const { data, error } = await supabase
      .from('quiz_games')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.error('Error fetching game:', error);
      return null;
    }
    
    if (!data) return null;
    
    return {
      ...data,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      createdBy: data.created_by
    } as QuizGame;
  });
}

export async function getGamesByUser(userId: string, limit = 20): Promise<QuizGame[]> {
  const { data, error } = await supabase
    .from('quiz_games')
    .select('*')
    .eq('created_by', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
    
  if (error) {
    console.error('Error fetching user games:', error);
    return [];
  }
  
  return data.map(game => ({
    ...game,
    createdAt: new Date(game.created_at),
    updatedAt: new Date(game.updated_at),
    createdBy: game.created_by
  })) as QuizGame[];
}

export async function getGamesByTags(tags: string[], limit = 20): Promise<QuizGame[]> {
  const { data, error } = await supabase
    .from('quiz_games')
    .select('*')
    .contains('tags', tags)
    .eq('is_public', true)
    .order('play_count', { ascending: false })
    .limit(limit);
    
  if (error) {
    console.error('Error fetching games by tags:', error);
    return [];
  }
  
  return data.map(game => ({
    ...game,
    createdAt: new Date(game.created_at),
    updatedAt: new Date(game.updated_at),
    createdBy: game.created_by
  })) as QuizGame[];
}

export async function createGame(input: QuizGameCreateInput, userId: string): Promise<QuizGame | null> {
  const { data, error } = await supabase
    .from('quiz_games')
    .insert({
      title: input.title,
      description: input.description || null,
      type: input.type,
      config: input.config || {},
      thumbnail: input.thumbnail || null,
      tags: input.tags || [],
      created_by: userId,
      is_public: input.isPublic || false
    })
    .select()
    .single();
    
  if (error) {
    console.error('Error creating game:', error);
    return null;
  }
  
  return {
    ...data,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    createdBy: data.created_by
  } as QuizGame;
}

export async function updateGame(id: string, input: QuizGameUpdateInput): Promise<QuizGame | null> {
  const updates: any = {};
  
  if (input.title !== undefined) updates.title = input.title;
  if (input.description !== undefined) updates.description = input.description;
  if (input.type !== undefined) updates.type = input.type;
  if (input.config !== undefined) updates.config = input.config;
  if (input.thumbnail !== undefined) updates.thumbnail = input.thumbnail;
  if (input.tags !== undefined) updates.tags = input.tags;
  if (input.isPublic !== undefined) updates.is_public = input.isPublic;
  
  updates.updated_at = new Date();
  
  const { data, error } = await supabase
    .from('quiz_games')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error('Error updating game:', error);
    return null;
  }
  
  // Clear cache
  const cacheKey = `quiz_game_${id}`;
  setCache(cacheKey, null);
  
  return {
    ...data,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    createdBy: data.created_by
  } as QuizGame;
}

export async function incrementPlayCount(gameId: string): Promise<void> {
  const { error } = await supabase.rpc('increment_game_play_count', {
    game_id: gameId
  });
  
  if (error) {
    console.error('Error incrementing play count:', error);
  }
  
  // Clear cache
  const cacheKey = `quiz_game_${gameId}`;
  setCache(cacheKey, null);
}

export async function getPopularGames(limit = 10): Promise<QuizGame[]> {
  const { data, error } = await supabase
    .from('quiz_games')
    .select('*')
    .eq('is_public', true)
    .order('play_count', { ascending: false })
    .limit(limit);
    
  if (error) {
    console.error('Error fetching popular games:', error);
    return [];
  }
  
  return data.map(game => ({
    ...game,
    createdAt: new Date(game.created_at),
    updatedAt: new Date(game.updated_at),
    createdBy: game.created_by
  })) as QuizGame[];
}

// CRUD cho Quiz Questions
export async function getQuestionsForGame(gameId: string): Promise<QuizQuestion[]> {
  const cacheKey = `quiz_questions_${gameId}`;
  
  return getCacheOrFetch(cacheKey, async () => {
    console.log(`Fetching questions for game ${gameId}`);
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('game_id', gameId)
      .order('id', { ascending: true });
      
    if (error) {
      console.error('Error fetching questions:', error);
      return [];
    }
    
    console.log(`Retrieved ${data ? data.length : 0} questions for game ${gameId}`, data);
    
    // Nếu không có câu hỏi, trả về câu hỏi mẫu cho game này
    if (!data || data.length === 0) {
      console.log(`No questions found for game ${gameId}, returning sample questions`);
      
      // Tạo câu hỏi mẫu cho game với UUID hợp lệ
      const sampleQuestions = [
        {
          id: crypto.randomUUID(), // Sử dụng UUID hợp lệ thay vì nanoid
          game_id: gameId,
          question: 'Thiền sư nào thời Lý nói tiếng với những con vật?',
          type: 'multiple_choice',
          options: ['Vạn Hạnh', 'Không Lộ', 'Man Giác', 'Viên Chiếu'],
          correct_answer: 'Vạn Hạnh',
          explanation: 'Thiền sư Vạn Hạnh thời Lý nổi tiếng với khả năng giao tiếp với động vật',
          order: 0, // Giữ lại thông tin để tương thích với interface
          points: 10
        },
        {
          id: crypto.randomUUID(),
          game_id: gameId,
          question: 'Dưới triều đại nào Phật giáo phát triển cực thịnh ở Việt Nam?',
          type: 'multiple_choice',
          options: ['Nhà Ngô', 'Nhà Đinh', 'Nhà Lý', 'Nhà Trần'],
          correct_answer: 'Nhà Lý',
          explanation: 'Dưới triều Lý, Phật giáo được coi là quốc giáo và phát triển mạnh mẽ',
          order: 1,
          points: 10
        },
        {
          id: crypto.randomUUID(),
          game_id: gameId,
          question: 'Ngôi chùa độc đáo với kiến trúc một cột ở Việt Nam là?',
          type: 'multiple_choice',
          options: ['Bảo Thiên Tự', 'Diên Hựu Tự', 'Phổ Minh', 'Thiên Mụ'],
          correct_answer: 'Diên Hựu Tự',
          explanation: 'Chùa Một Cột hay còn gọi là Diên Hựu Tự, được xây dựng năm 1049 dưới triều vua Lý Thái Tông',
          order: 2,
          points: 10
        },
        {
          id: crypto.randomUUID(),
          game_id: gameId,
          question: 'Tác phẩm nào được xem là nguồn sử liệu quan trọng của Phật giáo Việt Nam?',
          type: 'multiple_choice',
          options: ['Khóa Hư Lục', 'Thiền Uyển Tập Anh', 'Cư Trần Lạc Đạo Phú', 'Tất cả đáp án trên'],
          correct_answer: 'Thiền Uyển Tập Anh',
          explanation: 'Thiền Uyển Tập Anh là tác phẩm ghi chép về lịch sử truyền thừa của các vị tổ sư Thiền tông tại Việt Nam',
          order: 3,
          points: 10
        },
        {
          id: crypto.randomUUID(),
          game_id: gameId,
          question: 'Ba dòng thiền chính tồn tại và phát triển ở Việt Nam là gì?',
          type: 'multiple_choice',
          options: ['Tỳ Ni Đa Lưu Chi, Vô Ngôn Thông, Thảo Đường', 'Lâm Tế, Tào Động, Quy Ngưỡng', 'Tào Khê, Lâm Tế, Trúc Lâm', 'Nam Tông, Bắc Tông, Mật Tông'],
          correct_answer: 'Tỳ Ni Đa Lưu Chi, Vô Ngôn Thông, Thảo Đường',
          explanation: 'Ba dòng thiền chính trong lịch sử Phật giáo Việt Nam là Tỳ Ni Đa Lưu Chi, Vô Ngôn Thông và Thảo Đường',
          order: 4,
          points: 10
        }
      ];
      
      console.log('Sample questions generated:', sampleQuestions);
      return sampleQuestions;
    }
    
    const formattedQuestions = data.map(question => {
      // Chuyển đổi định dạng options từ object thành string[]
      let formattedOptions: string[] = [];
      if (Array.isArray(question.options)) {
        formattedOptions = typeof question.options[0] === 'string'
          ? question.options
          : question.options.map((opt: any) => opt.text || String(opt));
      }

      // Map dữ liệu từ DB sang định dạng QuizQuestion
      return {
        id: question.id,
        game_id: question.game_id,
        question: question.question_text || question.question, // Hỗ trợ cả hai trường
        type: question.type || 'multiple_choice',
        options: formattedOptions,
        correct_answer: question.correct_answer,
        explanation: question.explanation,
        order: question.order_index || 0, // Sửa sang order_index nếu có
        media_url: question.media,
        media_type: question.media_type,
        points: question.points || 10
      } as QuizQuestion;
    });
    
    console.log('Formatted questions:', formattedQuestions);
    return formattedQuestions;
  });
}

export async function getQuestionById(id: string): Promise<QuizQuestion | null> {
  const { data, error } = await supabase
    .from('quiz_questions')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error('Error fetching question:', error);
    return null;
  }
  
  return {
    ...data,
    gameId: data.game_id,
    nodeId: data.node_id,
    questionText: data.question_text,
    correctAnswer: data.correct_answer,
    mediaType: data.media_type,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at)
  } as QuizQuestion;
}

export async function addQuestionToGame(input: QuizQuestionCreateInput): Promise<QuizQuestion | null> {
  try {
    // Kiểm tra và tạo UUID mới cho nodeId nếu nó không phải là UUID hợp lệ
    let validNodeId = null;
    
    if (input.nodeId) {
      // Kiểm tra xem nodeId có phải là UUID hợp lệ không
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(input.nodeId)) {
        console.log(`NodeId không phải UUID hợp lệ: ${input.nodeId}, tạo mới UUID`);
        validNodeId = crypto.randomUUID(); // Tạo UUID mới hợp lệ
      } else {
        validNodeId = input.nodeId;
      }
    }
    
    // Kiểm tra và xử lý options
    let validOptions = [];
    
    if (input.options) {
      // Kiểm tra xem options có phải là array không
      if (Array.isArray(input.options)) {
        validOptions = input.options;
      } else {
        // Nếu là object, chuyển đổi sang array
        console.log('Options không phải array, chuyển đổi:', input.options);
        validOptions = [input.options];
      }
      
      // Đảm bảo mỗi option có ID
      validOptions = validOptions.map(option => ({
        ...option,
        id: option.id || nanoid(8)
      }));
    } else {
      validOptions = [];
    }
    
    // Thêm question vào database với các giá trị đã xử lý
    const { data, error } = await supabase
      .from('quiz_questions')
      .insert({
        game_id: input.gameId,
        node_id: validNodeId,
        question_text: input.questionText,
        options: validOptions,
        correct_answer: input.correctAnswer,
        explanation: input.explanation || null,
        difficulty: input.difficulty || 1,
        points: input.points || 10,
        media: input.media || null,
        media_type: input.mediaType || null
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error adding question:', error);
      return null;
    }
    
    // Clear cache
    const cacheKey = `quiz_questions_${input.gameId}`;
    setCache(cacheKey, null);
    
    return {
      ...data,
      gameId: data.game_id,
      nodeId: data.node_id,
      questionText: data.question_text,
      correctAnswer: data.correct_answer,
      mediaType: data.media_type,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    } as QuizQuestion;
  } catch (error) {
    console.error('Exception in addQuestionToGame:', error);
    return null;
  }
}

export async function updateQuestion(id: string, input: QuizQuestionUpdateInput): Promise<QuizQuestion | null> {
  const updates: any = {};
  
  if (input.questionText !== undefined) updates.question_text = input.questionText;
  if (input.options !== undefined) updates.options = input.options;
  if (input.correctAnswer !== undefined) updates.correct_answer = input.correctAnswer;
  if (input.explanation !== undefined) updates.explanation = input.explanation;
  if (input.difficulty !== undefined) updates.difficulty = input.difficulty;
  if (input.points !== undefined) updates.points = input.points;
  if (input.media !== undefined) updates.media = input.media;
  if (input.mediaType !== undefined) updates.media_type = input.mediaType;
  
  updates.updated_at = new Date();
  
  const { data, error } = await supabase
    .from('quiz_questions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error('Error updating question:', error);
    return null;
  }
  
  // Get game ID to clear cache
  const gameId = data.game_id;
  const cacheKey = `quiz_questions_${gameId}`;
  setCache(cacheKey, null);
  
  return {
    ...data,
    gameId: data.game_id,
    nodeId: data.node_id,
    questionText: data.question_text,
    correctAnswer: data.correct_answer,
    mediaType: data.media_type,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at)
  } as QuizQuestion;
}

export async function deleteQuestion(id: string): Promise<boolean> {
  // First get the question to know its gameId for cache clearing
  const { data: question, error: getError } = await supabase
    .from('quiz_questions')
    .select('game_id')
    .eq('id', id)
    .single();
    
  if (getError) {
    console.error('Error getting question for deletion:', getError);
    return false;
  }
  
  const { error } = await supabase
    .from('quiz_questions')
    .delete()
    .eq('id', id);
    
  if (error) {
    console.error('Error deleting question:', error);
    return false;
  }
  
  // Clear cache
  const cacheKey = `quiz_questions_${question.game_id}`;
  setCache(cacheKey, null);
  
  return true;
}

// Quiz Game Progress
export async function startQuizGame(userId: string, gameId: string): Promise<QuizUserProgress | null> {
  console.log(`Starting quiz game - User: ${userId}, Game: ${gameId}`);
  
  // Lưu thêm metadata cho userID
  const metadata = {
    isAnonymous: !userId.includes('-') || userId.length < 36
  };
  
  // Check if there's an existing progress
  const { data: existing, error: existingError } = await supabase
    .from('quiz_user_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .single();
    
    // Chỉ tăng lượt chơi khi bắt đầu game mới hoặc game cũ đã hoàn thành
    const shouldIncrementPlayCount = !existing || !!existing.completed_at;
    
    if (shouldIncrementPlayCount) {
      try {
        await incrementPlayCount(gameId);
        console.log('Incremented play count for game:', gameId);
      } catch (error) {
        console.error('Error incrementing play count:', error);
        // Không return null ở đây để vẫn tiếp tục start game dù không tăng được count
      }
    } else {
      console.log('Not incrementing play count - resuming existing game');
    }
    
    if (!existingError && existing) {
      console.log('Found existing progress:', existing);
      
      // Return existing progress if not completed
      if (!existing.completed_at) {
        console.log('Existing progress is not completed, resuming');
        
        return {
          ...existing,
          userId: existing.user_id,
          gameId: existing.game_id,
          score: existing.score || 0,
          completed: !!existing.completed_at,
          current_question_index: (existing.completed_questions || []).length,
          correct_answers: 0, // Tạm tính bằng 0
          total_questions: 0, // Sẽ được cập nhật sau
          completed_questions: existing.completed_questions || [],
          current_question_id: existing.current_question_id,
          startedAt: new Date(existing.started_at).toISOString(),
          completedAt: existing.completed_at ? new Date(existing.completed_at).toISOString() : undefined
        } as QuizUserProgress;
      }
      
      console.log('Existing progress is completed, creating new');
      
      // If completed, delete and create new
      await supabase
        .from('quiz_user_progress')
        .delete()
        .eq('id', existing.id);
    }
  
  // Get all questions to count
  const { data: allQuestions, error: allQuestionsError } = await supabase
    .from('quiz_questions')
    .select('id')
    .eq('game_id', gameId);
    
  if (allQuestionsError) {
    console.error('Error fetching questions:', allQuestionsError);
    return null;
  }
  
  const totalQuestions = allQuestions?.length || 0;
  console.log(`Total questions for game: ${totalQuestions}`);
  
  // Create new progress
  const progressData = {
    user_id: userId,
    game_id: gameId,
    score: 0,
    completed_questions: [],
    current_question_id: null,
    started_at: new Date().toISOString()
  };
  
  const { data, error } = await supabase
    .from('quiz_user_progress')
    .insert(progressData)
    .select()
    .single();
    
  if (error) {
    console.error('Error creating progress:', error);
    return null;
  }
  
  console.log('Created new progress:', data);
  
  return {
    ...data,
    userId: data.user_id,
    gameId: data.game_id,
    completed: false, // Mới bắt đầu nên chưa hoàn thành
    current_question_index: 0,
    correct_answers: 0,
    total_questions: totalQuestions,
    completed_questions: data.completed_questions || [],
    current_question_id: data.current_question_id,
    startedAt: new Date(data.started_at).toISOString(),
    completedAt: undefined
  } as QuizUserProgress;
}

export async function updateQuizProgress(
  userId: string, 
  gameId: string, 
  questionId: string, 
  isCorrect: boolean
): Promise<QuizUserProgress | null> {
  console.log(`Updating progress - User: ${userId}, Game: ${gameId}, Question: ${questionId}, Correct: ${isCorrect}`);
  
  // Get current progress
  const { data: current, error: currentError } = await supabase
    .from('quiz_user_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .single();
    
  if (currentError) {
    console.error('Error getting current progress:', currentError);
    return null;
  }
  
  console.log('Current progress:', current);
  
  // Kiểm tra nếu đã hoàn thành
  if (current.completed_at) {
    console.log('Quiz already completed, cannot update progress');
    return {
      ...current,
      userId: current.user_id,
      gameId: current.game_id,
      score: current.score,
      completed: true,
      current_question_index: (current.completed_questions || []).length,
      correct_answers: current.correct_answers || 0,
      completed_questions: current.completed_questions || [],
      current_question_id: current.current_question_id,
      startedAt: new Date(current.started_at).toISOString(),
      completedAt: new Date(current.completed_at).toISOString()
    } as QuizUserProgress;
  }
  
  // Check if question already answered
  const completedQuestions = current.completed_questions || [];
  
  if (completedQuestions.includes(questionId)) {
    console.log('Question already answered, skipping update');
    return {
      ...current,
      userId: current.user_id,
      gameId: current.game_id,
      score: current.score,
      completed: !!current.completed_at,
      current_question_index: completedQuestions.length,
      correct_answers: current.correct_answers || 0,
      total_questions: 0, // Will be updated later
      completed_questions: completedQuestions,
      current_question_id: current.current_question_id,
      startedAt: new Date(current.started_at).toISOString(),
      completedAt: current.completed_at ? new Date(current.completed_at).toISOString() : undefined
    } as QuizUserProgress;
  }
  
  // Add to completed questions
  const newCompletedQuestions = [...completedQuestions, questionId];
  
  // Get all questions to check if this was the last one
  const { data: allQuestions, error: questionsError } = await supabase
    .from('quiz_questions')
    .select('id')
    .eq('game_id', gameId);
    
  if (questionsError) {
    console.error('Error getting questions:', questionsError);
    return null;
  }
  
  const questionCount = allQuestions?.length || 0;
  const isLastQuestion = newCompletedQuestions.length >= questionCount;
  
  // Update score - basic implementation
  const scoreIncrement = isCorrect ? 10 : 0; // 10 points per correct answer
  const newScore = current.score + scoreIncrement;
  
  // Get next question if not the last one
  let nextQuestionId = null;
  
  if (!isLastQuestion) {
    const answeredIds = new Set(newCompletedQuestions);
    const remainingQuestions = allQuestions.filter(q => !answeredIds.has(q.id));
    
    if (remainingQuestions.length > 0) {
      // Pick first unanswered question (can be randomized later)
      nextQuestionId = remainingQuestions[0].id;
    }
  }
  
  // Update progress in database
  const updateData: any = {
    score: newScore,
    completed_questions: newCompletedQuestions,
    current_question_id: nextQuestionId,
  };
  
  // If this was the last question, mark as completed
  if (isLastQuestion) {
    updateData.completed_at = new Date().toISOString();
  }
  
  // Tính toán số câu trả lời đúng
  let correctAnswers = current.correct_answers || 0;
  if (isCorrect) {
    correctAnswers += 1;
  }
  updateData.correct_answers = correctAnswers;
  
  console.log('Updating with data:', updateData);
  
  const { data, error } = await supabase
    .from('quiz_user_progress')
    .update(updateData)
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating progress:', error);
    return null;
  }
  
  console.log('Progress updated successfully:', data);
  
  // Get total questions count
  const { data: allQuestionsCount } = await supabase
    .from('quiz_questions')
    .select('id')
    .eq('game_id', gameId);
  
  const totalQuestions = allQuestionsCount?.length || 0;
  
  return {
    ...data,
    userId: data.user_id,
    gameId: data.game_id,
    score: data.score,
    completed: !!data.completed_at,
    current_question_index: (data.completed_questions || []).length,
    correct_answers: data.correct_answers || 0,
    total_questions: totalQuestions,
    completed_questions: data.completed_questions || [],
    current_question_id: data.current_question_id,
    startedAt: new Date(data.started_at).toISOString(),
    completedAt: data.completed_at ? new Date(data.completed_at).toISOString() : undefined
  } as QuizUserProgress;
}

export async function getUserProgress(userId: string, gameId: string): Promise<QuizUserProgress | null> {
  const { data, error } = await supabase
    .from('quiz_user_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .single();
    
  if (error) {
    if (error.code === 'PGRST116') {
      // No progress found
      return null;
    }
    console.error('Error getting user progress:', error);
    return null;
  }
  
  // Get total questions count
  const { data: allQuestions } = await supabase
    .from('quiz_questions')
    .select('id')
    .eq('game_id', gameId);
  
  const totalQuestions = allQuestions?.length || 0;
  
  return {
    ...data,
    userId: data.user_id,
    gameId: data.game_id,
    score: data.score,
    completed: !!data.completed_at,
    current_question_index: (data.completed_questions || []).length,
    correct_answers: data.correct_answers || 0,
    total_questions: totalQuestions,
    completed_questions: data.completed_questions || [],
    current_question_id: data.current_question_id,
    startedAt: new Date(data.started_at).toISOString(),
    completedAt: data.completed_at ? new Date(data.completed_at).toISOString() : undefined
  } as QuizUserProgress;
}

export async function getUserAllProgress(userId: string): Promise<QuizUserProgress[]> {
  const { data, error } = await supabase
    .from('quiz_user_progress')
    .select('*, quiz_games(title, thumbnail)')
    .eq('user_id', userId)
    .order('started_at', { ascending: false });
    
  if (error) {
    console.error('Error getting user progress:', error);
    return [];
  }
  
  return data.map(item => ({
    ...item,
    userId: item.user_id,
    gameId: item.game_id,
    score: item.score,
    completed: !!item.completed_at,
    current_question_index: (item.completed_questions || []).length,
    correct_answers: item.correct_answers || 0,
    total_questions: item.total_questions || 0, // May need to be fetched for accuracy
    startedAt: new Date(item.started_at).toISOString(),
    completedAt: item.completed_at ? new Date(item.completed_at).toISOString() : undefined
  })) as QuizUserProgress[];
}

// Helper function to mask username for privacy
function maskedUsername(username: string): string {
  if (!username) return '';
  
  // Nếu username quá ngắn, thêm "Player-" prefix
  if (username.length < 5) {
    return `Player-${username}`;
  }
  
  // Giữ lại 2 ký tự đầu và 2 ký tự cuối, thay thế phần giữa bằng dấu '*'
  const firstChars = username.substring(0, 2);
  const lastChars = username.substring(username.length - 2);
  const middleLength = username.length - 4;
  const maskedMiddle = '*'.repeat(Math.min(middleLength, 5)); // Giới hạn tối đa 5 dấu sao
  
  return `${firstChars}${maskedMiddle}${lastChars}`;
}

// Leaderboard
export async function getLeaderboard(gameId: string, limit = 10): Promise<QuizLeaderboardEntry[]> {
  try {
    // Log để debug
    console.log(`Fetching leaderboard for game ${gameId} with limit ${limit}`);
    
    // Lấy dữ liệu từ bảng quiz_leaderboard mà không join với profiles
    const { data, error } = await supabase
      .from('quiz_leaderboard')
      .select('*')
      .eq('game_id', gameId)
      .order('score', { ascending: false })
      .order('time_taken', { ascending: true })
      .limit(limit);
      
    if (error) {
      console.error('Error getting leaderboard:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      return [];
    }
    
    if (!data || data.length === 0) {
      console.log(`No leaderboard entries found for game ${gameId}`);
      // Thực hiện truy vấn debug để kiểm tra table có dữ liệu không
      try {
        const { count, error: countError } = await supabase
          .from('quiz_leaderboard')
          .select('*', { count: 'exact', head: true });
        
        console.log(`Total entries in quiz_leaderboard table: ${count}`);
        if (countError) {
          console.error('Error counting leaderboard entries:', countError);
        }
        
        // Kiểm tra RLS policy
        const { data: authData } = await supabase.auth.getSession();
        console.log('Current auth session:', authData);
      } catch (debugError) {
        console.error('Error during debug queries:', debugError);
      }
      
      return [];
    }
    
    console.log(`Retrieved ${data.length} leaderboard entries:`, data);
    
    // Lấy thông tin các user từ bảng users thay vì profiles
    const userIds = data.map(entry => entry.user_id);
    const uniqueUserIds = [...new Set(userIds)];
    
    let usersMap: Record<string, any> = {};
    // Tạo danh sách người dùng ẩn danh
    let anonymousUsers = new Set<string>();

    if (uniqueUserIds.length > 0) {
      try {
        const { data: usersData, error: usersError } = await supabase
          .from('users')  // Sửa từ 'profiles' thành 'users'
          .select('id, username, avatar_url')
          .in('id', uniqueUserIds);
          
        if (!usersError && usersData) {
          // Tạo map từ user_id đến user data
          usersMap = usersData.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
          }, {} as Record<string, any>);
          
          // Xác định những người dùng không có trong bảng users (có thể là anonymous)
          uniqueUserIds.forEach(id => {
            if (!usersMap[id]) {
              anonymousUsers.add(id);
            }
          });
        } else {
          console.error('Error getting users:', usersError);
          // Nếu không lấy được dữ liệu, coi tất cả là anonymous
          uniqueUserIds.forEach(id => anonymousUsers.add(id));
        }
      } catch (userError) {
        console.error('Exception getting users:', userError);
        // Nếu lỗi, coi tất cả là anonymous
        uniqueUserIds.forEach(id => anonymousUsers.add(id));
      }
    }
    
    // Chuyển đổi dữ liệu thành định dạng QuizLeaderboardEntry
    return data.map(entry => {
      // Kiểm tra nếu là anonymous user (không có trong usersMap)
      const isAnonymousUser = anonymousUsers.has(entry.user_id);
      let username, avatarUrl;
      
      if (isAnonymousUser) {
        // Tạo tên cho người dùng ẩn danh
        const randomId = entry.user_id.substring(0, 4);
        username = `Người chơi ẩn danh ${randomId}`;
        avatarUrl = ''; // Có thể dùng avatar mặc định
      } else {
        const userData = usersMap[entry.user_id] || {};
        // Sử dụng username từ user data hoặc tạo username từ user_id
        username = userData.username || `Player-${entry.user_id.substring(0, 4)}`;
        // Ẩn một phần tên người dùng để bảo vệ quyền riêng tư nếu không phải anonymous
        username = maskedUsername(username);
        avatarUrl = userData.avatar_url || '';
      }
      
      // Chuyển đổi dữ liệu về đúng kiểu
      const result: QuizLeaderboardEntry = {
        id: entry.id,
        user_id: entry.user_id,
        game_id: entry.game_id,
        score: entry.score,
        time_taken: entry.time_taken || 0, // Đảm bảo không bị null
        created_at: entry.completed_at || entry.created_at, // Sử dụng completed_at nếu có
        
        // Thêm các thuộc tính user profile
        userProfile: {
          username: username,
          avatar: avatarUrl // Sử dụng avatar URL từ user data
        }
      };
      
      return result;
    });
  } catch (error) {
    console.error('Unexpected error getting leaderboard:', error);
    return [];
  }
}

// Utilities
export async function convertNodeToQuizQuestion(nodeId: string, gameId: string): Promise<QuizQuestion | null> {
  const { data: node, error } = await supabase
    .from('learning_nodes')
    .select('*')
    .eq('id', nodeId)
    .single();
    
  if (error) {
    console.error('Error fetching node:', error);
    return null;
  }
  
  // Parse node data for quiz question
  const options: QuizOption[] = [];
  let correctAnswer: any = null;
  
  // For multiple choice, use node choices as options
  if (node.choices && Array.isArray(node.choices)) {
    node.choices.forEach((choice: any, index: number) => {
      const isCorrect = index === 0; // Make first choice correct by default
      const option: QuizOption = {
        id: nanoid(8),
        text: choice.text || 'Option',
        isCorrect
      };
      
      options.push(option);
      
      if (isCorrect) {
        correctAnswer = option.id;
      }
    });
  }
  
  // For flashcards, create front/back
  if (options.length === 0) {
    const frontOption: QuizOption = {
      id: nanoid(8),
      text: node.title,
      isCorrect: false
    };
    
    const backOption: QuizOption = {
      id: nanoid(8),
      text: node.content,
      isCorrect: true
    };
    
    options.push(frontOption, backOption);
    correctAnswer = {
      front: frontOption.id,
      back: backOption.id
    };
  }
  
  const questionData: QuizQuestionCreateInput = {
    gameId,
    nodeId,
    questionText: node.title,
    options: options,
    correctAnswer,
    explanation: node.content,
    difficulty: 1,
    points: 10
  };
  
  return addQuestionToGame(questionData);
}

export async function batchConvertNodesToQuestions(nodeIds: string[], gameId: string): Promise<number> {
  let successCount = 0;
  
  for (const nodeId of nodeIds) {
    const result = await convertNodeToQuizQuestion(nodeId, gameId);
    if (result) successCount++;
  }
  
  return successCount;
}

export async function completeQuizGame(
  userId: string, 
  gameId: string, 
  finalScore: number, 
  timeSpent: number, 
  userAnswers?: any[]
): Promise<boolean> {
  console.log(`Completing quiz game - User: ${userId}, Game: ${gameId}, Score: ${finalScore}, Time: ${timeSpent}, Answers: `, userAnswers);
  
  try {
    // Kiểm tra nếu đây là anonymous user, sẽ lưu kết quả vào một user pool chung
    const isAnonymousUser = userId.startsWith('anonymous-');
    // Sử dụng userId đã cung cấp, hoặc 'anonymous-user' nếu là người dùng ẩn danh 
    // Chú ý: anonymous user sẽ không được hiển thị trong leaderboard
    
    // Convert timeSpent to integer if needed
    const timeSpentInt = Math.floor(Number(timeSpent));
    
    // Đếm số câu trả lời đúng từ userAnswers nếu có
    let correctAnswersCount = 0;
    if (userAnswers && Array.isArray(userAnswers)) {
      correctAnswersCount = userAnswers.filter(answer => answer.isCorrect).length;
      console.log(`Calculated correct answers: ${correctAnswersCount} out of ${userAnswers.length}`);
    }
    
    // Format userAnswers as JSONB if provided
    const formattedUserAnswers = userAnswers ? JSON.stringify(userAnswers) : null;
    
    console.log('Calling complete_quiz_game with params:', {
      userId, gameId, finalScore, timeSpentInt, correctAnswers: correctAnswersCount, formattedUserAnswers
    });
    
    // Kiểm tra xem đã có bản ghi progress chưa
    const { data: existingProgress, error: progressCheckError } = await supabase
      .from('quiz_user_progress')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .maybeSingle();
      
    if (progressCheckError) {
      console.error('Error checking existing progress:', progressCheckError);
    }
    
    // Cập nhật hoặc thêm mới tùy thuộc vào kết quả
    if (existingProgress) {
      console.log(`Updating existing progress record: ${existingProgress.id}`);
      
      // Update existing record
      const { error: updateError } = await supabase
        .from('quiz_user_progress')
        .update({
          score: finalScore,
          completed_at: new Date().toISOString(),
          time_spent: timeSpentInt,
          user_answers: formattedUserAnswers,
          correct_answers: correctAnswersCount,
          total_questions: userAnswers?.length || 0
        })
        .eq('id', existingProgress.id);
        
      if (updateError) {
        console.error('Error updating quiz progress:', updateError);
        return false;
      }
    } else {
      console.log('Creating new progress record');
      
      // Insert new record
      const { error: insertError } = await supabase
        .from('quiz_user_progress')
        .insert({
          user_id: userId,
          game_id: gameId,
          score: finalScore,
          time_spent: timeSpentInt,
          completed_at: new Date().toISOString(),
          user_answers: formattedUserAnswers,
          correct_answers: correctAnswersCount,
          total_questions: userAnswers?.length || 0
        });
        
      if (insertError) {
        console.error('Error inserting quiz progress:', insertError);
        return false;
      }
    }
    
    // Kiểm tra và cập nhật leaderboard
    const { data: existingLeaderboard, error: leaderboardCheckError } = await supabase
      .from('quiz_leaderboard')
      .select('id, score')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .maybeSingle();
      
    if (leaderboardCheckError) {
      console.error('Error checking existing leaderboard:', leaderboardCheckError);
    }
    
    // Chỉ cập nhật leaderboard nếu điểm mới cao hơn hoặc chưa có
    // Nếu là anonymous user vẫn lưu vào leaderboard để có dữ liệu hiển thị
    if (!existingLeaderboard || finalScore > existingLeaderboard.score) {
      if (existingLeaderboard) {
        console.log(`Updating leaderboard with higher score: ${finalScore} > ${existingLeaderboard.score}`);
        
        // Update với điểm cao hơn
        const { error: updateLeaderboardError } = await supabase
          .from('quiz_leaderboard')
          .update({
            score: finalScore,
            time_taken: timeSpentInt,
            completed_at: new Date().toISOString()
          })
          .eq('id', existingLeaderboard.id);
          
        if (updateLeaderboardError) {
          console.error('Error updating leaderboard:', updateLeaderboardError);
        }
      } else {
        console.log('Adding new leaderboard entry');
        
        // Insert mới vào leaderboard
        const { error: insertLeaderboardError } = await supabase
          .from('quiz_leaderboard')
          .insert({
            user_id: userId,
            game_id: gameId,
            score: finalScore,
            time_taken: timeSpentInt,
            completed_at: new Date().toISOString()
          });
          
        if (insertLeaderboardError) {
          console.error('Error inserting leaderboard:', insertLeaderboardError);
        }
      }
    } else {
      console.log(`Not updating leaderboard as new score is not higher: ${finalScore} <= ${existingLeaderboard.score}`);
    }
    
    console.log('Quiz game completed successfully');
    return true;
  } catch (error) {
    console.error('Unexpected error in completeQuizGame:', error);
    return false;
  }
}

// Hàm lấy chi tiết kết quả của một lần làm quiz
export async function getQuizResultDetails(resultId: string): Promise<any> {
  try {
    console.log(`Fetching quiz result details for ID ${resultId}`);
    
    // Lấy thông tin từ bảng quiz_user_progress
    const { data: progressData, error: progressError } = await supabase
      .from('quiz_user_progress')
      .select(`
        *,
        quiz_games(id, title, description, thumbnail, type)
      `)
      .eq('id', resultId)
      .single();
      
    if (progressError) {
      console.error('Error fetching quiz result details:', progressError);
      return null;
    }
    
    if (!progressData) {
      console.log(`No quiz result found with ID ${resultId}`);
      return null;
    }
    
    // Lấy danh sách câu hỏi của quiz này
    const { data: questionsData, error: questionsError } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('game_id', progressData.game_id);
      
    if (questionsError) {
      console.error('Error fetching questions for quiz result:', questionsError);
      // Vẫn tiếp tục xử lý
    }
    
    // Lấy thông tin người dùng
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, username, avatar_url')
      .eq('id', progressData.user_id)
      .single();
      
    if (userError) {
      console.error('Error fetching user data for quiz result:', userError);
      // Vẫn tiếp tục xử lý
    }
    
    // Cấu trúc kết quả trả về
    const result = {
      id: progressData.id,
      userId: progressData.user_id,
      gameId: progressData.game_id,
      score: progressData.score,
      timeSpent: progressData.time_spent,
      completedAt: progressData.completed_at,
      startedAt: progressData.started_at,
      completedQuestions: progressData.completed_questions || [],
      user: userData || null,
      game: progressData.quiz_games,
      questions: questionsData || [],
      userAnswers: progressData.user_answers || [], // Lưu ý: cần cập nhật schema để lưu câu trả lời
      correctAnswers: progressData.correct_answers || 0,
      totalQuestions: questionsData?.length || 0
    };
    
    console.log('Quiz result details retrieved successfully:', result);
    return result;
  } catch (error) {
    console.error('Unexpected error in getQuizResultDetails:', error);
    return null;
  }
}

// Hàm lấy tất cả kết quả quiz của một người dùng
export async function getQuizResultsByUser(userId: string, limit = 20, page = 1): Promise<any[]> {
  try {
    console.log(`[QuizService] Fetching quiz results for user ${userId}, page ${page}, limit ${limit}`);
    
    if (!userId) {
      console.error('[QuizService] userId is undefined or null');
      return [];
    }
    
    const offset = (page - 1) * limit;
    
    // Debug query trước khi chạy
    console.log(`[QuizService] Running query with: user_id=${userId}, completed_at IS NOT NULL, offset=${offset}, limit=${limit}`);
    
    // Lấy kết quả từ bảng quiz_user_progress
    const { data: progressData, error: progressError } = await supabase
      .from('quiz_user_progress')
      .select(`
        *,
        quiz_games(id, title, description, thumbnail, type)
      `)
      .eq('user_id', userId)
      .not('completed_at', 'is', null) // Sửa lại cú pháp is.not.null
      .order('completed_at', { ascending: false })
      .range(offset, offset + limit - 1);
      
    if (progressError) {
      console.error('[QuizService] Error fetching user quiz results:', progressError);
      return [];
    }
    
    console.log(`[QuizService] Raw progress data:`, progressData);
    
    // Kiểm tra có dữ liệu hay không
    if (!progressData || progressData.length === 0) {
      console.log('[QuizService] No quiz results found for user', userId);
      
      // Debug: Thử kiểm tra xem user có tồn tại trong bảng quiz_user_progress hay không
      const { count, error: countError } = await supabase
        .from('quiz_user_progress')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
        
      if (countError) {
        console.error('[QuizService] Error checking user records count:', countError);
      } else {
        console.log(`[QuizService] Total records for user ${userId} in quiz_user_progress: ${count}`);
      }
      
      return [];
    }
    
    // Chuyển đổi dữ liệu
    const results = progressData.map(item => ({
      id: item.id,
      userId: item.user_id,
      gameId: item.game_id,
      game: item.quiz_games,
      score: item.score || 0,
      correctAnswers: item.correct_answers || 0,
      totalQuestions: item.total_questions || 0, 
      timeSpent: item.time_spent || 0,
      completedAt: item.completed_at,
      startedAt: item.started_at
    }));
    
    console.log(`[QuizService] Retrieved ${results.length} quiz results for user ${userId}`);
    return results;
  } catch (error) {
    console.error('[QuizService] Unexpected error in getQuizResultsByUser:', error);
    return [];
  }
}

// Hàm lấy tất cả kết quả của một quiz game
export async function getQuizResultsByGame(gameId: string, limit = 20, page = 1): Promise<any[]> {
  try {
    console.log(`Fetching quiz results for game ${gameId}, page ${page}, limit ${limit}`);
    
    const offset = (page - 1) * limit;
    
    // Lấy kết quả từ cả hai bảng: quiz_user_progress và quiz_leaderboard
    const { data: progressData, error: progressError } = await supabase
      .from('quiz_user_progress')
      .select(`
        *,
        users(id, username, avatar_url)
      `)
      .eq('game_id', gameId)
      .not('completed_at', 'is', null) // Sửa lại cú pháp is.not.null
      .order('score', { ascending: false })
      .order('time_spent', { ascending: true })
      .range(offset, offset + limit - 1);
      
    if (progressError) {
      console.error('Error fetching game quiz results:', progressError);
      return [];
    }
    
    // Chuyển đổi dữ liệu
    const results = progressData.map(item => ({
      id: item.id,
      userId: item.user_id,
      gameId: item.game_id,
      user: item.users,
      score: item.score,
      correctAnswers: item.correct_answers || 0,
      totalQuestions: item.total_questions || 0, 
      timeSpent: item.time_spent || 0,
      completedAt: item.completed_at,
      startedAt: item.started_at
    }));
    
    console.log(`Retrieved ${results.length} quiz results for game ${gameId}`);
    return results;
  } catch (error) {
    console.error('Unexpected error in getQuizResultsByGame:', error);
    return [];
  }
}

// Hàm dành cho admin để lấy tất cả kết quả quiz với bộ lọc
export async function getQuizResultsForAdmin(
  filters: {
    userId?: string, 
    gameId?: string, 
    startDate?: string, 
    endDate?: string,
    minScore?: number,
    maxScore?: number
  }, 
  limit = 20, 
  page = 1
): Promise<any> {
  try {
    console.log(`Fetching quiz results for admin with filters:`, filters);
    
    const offset = (page - 1) * limit;
    
    // Xây dựng query - sử dụng phương thức count() riêng biệt
    let query = supabase
      .from('quiz_user_progress')
      .select(`
        *,
        users(id, username, avatar_url),
        quiz_games(id, title, description, thumbnail, type)
      `)
      .not('completed_at', 'is', null); // Sửa lại cú pháp is.not.null
    
    // Query riêng để đếm tổng số kết quả
    let countQuery = supabase
      .from('quiz_user_progress')
      .select('*', { count: 'exact', head: true })
      .not('completed_at', 'is', null); // Sửa lại cú pháp is.not.null
    
    // Áp dụng các bộ lọc
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
      countQuery = countQuery.eq('user_id', filters.userId);
    }
    
    if (filters.gameId) {
      query = query.eq('game_id', filters.gameId);
      countQuery = countQuery.eq('game_id', filters.gameId);
    }
    
    if (filters.startDate) {
      query = query.gte('completed_at', filters.startDate);
      countQuery = countQuery.gte('completed_at', filters.startDate);
    }
    
    if (filters.endDate) {
      query = query.lte('completed_at', filters.endDate);
      countQuery = countQuery.lte('completed_at', filters.endDate);
    }
    
    if (filters.minScore !== undefined) {
      query = query.gte('score', filters.minScore);
      countQuery = countQuery.gte('score', filters.minScore);
    }
    
    if (filters.maxScore !== undefined) {
      query = query.lte('score', filters.maxScore);
      countQuery = countQuery.lte('score', filters.maxScore);
    }
    
    // Sắp xếp và phân trang
    query = query
      .order('completed_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    // Thực hiện truy vấn
    const [{ data, error }, { count, error: countError }] = await Promise.all([
      query,
      countQuery
    ]);
      
    if (error || countError) {
      console.error('Error fetching admin quiz results:', error || countError);
      return { results: [], total: 0 };
    }
    
    if (!data) {
      return { results: [], total: 0 };
    }
    
    // Chuyển đổi dữ liệu
    const results = data.map(item => ({
      id: item.id,
      userId: item.user_id,
      gameId: item.game_id,
      user: item.users,
      game: item.quiz_games,
      score: item.score,
      correctAnswers: item.correct_answers || 0,
      totalQuestions: item.total_questions || 0, 
      timeSpent: item.time_spent || 0,
      completedAt: item.completed_at,
      startedAt: item.started_at
    }));
    
    console.log(`Retrieved ${results.length} quiz results for admin, total: ${count}`);
    return { 
      results, 
      total: count || 0,
      page,
      limit,
      totalPages: count ? Math.ceil(count / limit) : 0
    };
  } catch (error) {
    console.error('Unexpected error in getQuizResultsForAdmin:', error);
    return { results: [], total: 0 };
  }
}

// Thêm function xóa quiz game
export async function deleteGame(id: string): Promise<boolean> {
  try {
    // Xóa quiz_user_progress trước
    const { error: progressError } = await supabase
      .from('quiz_user_progress')
      .delete()
      .eq('game_id', id);
      
    if (progressError) {
      console.error('Error deleting quiz user progress:', progressError);
      return false;
    }

    // Xóa quiz game
    const { error } = await supabase
      .from('quiz_games')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error('Error deleting quiz game:', error);
      return false;
    }
    
    // Clear cache
    const cacheKey = `quiz_game_${id}`;
    setCache(cacheKey, null);
    
    // Xóa cache cho questions nếu có
    const questionsCacheKey = `quiz_questions_${id}`;
    setCache(questionsCacheKey, null);
    
    return true;
  } catch (error) {
    console.error('Unexpected error deleting quiz game:', error);
    return false;
  }
}

// Flow Content Functions
export async function startFlowContentGame(
  userId: string,
  gameId: string
): Promise<QuizUserProgress> {
  try {
    console.log(`Starting flow content game for user ${userId} and game ${gameId}`);
    
    // Get first question
    console.log('Fetching first question...');
    let firstQuestion = await getFirstQuestionOfGame(gameId);
    
    if (!firstQuestion) {
      console.error('No questions found for this game, trying to create default question');
      
      // Thử tạo một câu hỏi mặc định trực tiếp
      const defaultQuestion = {
        id: crypto.randomUUID(),
        game_id: gameId,
        question_text: 'Bắt đầu hành trình của bạn',
        options: JSON.stringify({
          choices: [
            {
              id: 'choice-1',
              text: 'Tiếp tục',
              next_question_id: null,
              is_end_node: true,
              effect: {
                type: 'positive',
                value: 10,
                attribute: 'general',
                description: 'Bắt đầu hành trình'
              }
            }
          ],
          is_end_node: false
        }),
        correct_answer: null,
        explanation: 'Đây là câu hỏi mặc định cho flow content',
        difficulty: 1,
        points: 10,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log('Creating default question directly:', defaultQuestion);
      
      // Lưu câu hỏi vào database
      const { data: insertedQuestion, error: insertError } = await supabase
        .from('quiz_questions')
        .insert(defaultQuestion)
        .select()
        .single();
        
      if (insertError) {
        console.error('Error creating default question directly:', insertError);
        console.error('Error details:', JSON.stringify(insertError, null, 2));
        throw new Error('No questions found for this game and failed to create default question');
      }
      
      console.log('Created default question for flow content directly:', insertedQuestion);
      
      // Tạo một QuizQuestion từ insertedQuestion
      firstQuestion = {
        ...insertedQuestion,
        gameId: insertedQuestion.game_id,
        questionText: insertedQuestion.question_text,
        correctAnswer: insertedQuestion.correct_answer,
        mediaType: insertedQuestion.media_type,
        createdAt: new Date(insertedQuestion.created_at),
        updatedAt: new Date(insertedQuestion.updated_at)
      } as QuizQuestion;
    }
    
    console.log('Found/created first question:', firstQuestion.id);

    // Get or create progress
    console.log('Getting user progress...');
    let progress = await getUserProgress(userId, gameId);
    
    if (progress) {
      // If progress exists, check if it's completed
      if (progress.completedAt) {
        console.log('Previous progress was completed, creating new one');
        // Create a new progress if the game was completed
        progress = await createUserProgress(userId, gameId, firstQuestion.id);
      } else {
        console.log('Found existing incomplete progress:', progress);
      }
    } else {
      // Create new progress if none exists
      console.log('No existing progress, creating new one');
      progress = await createUserProgress(userId, gameId, firstQuestion.id);
    }
    
    // Update progress to mark game as started
    const now = new Date().toISOString();
    
    console.log('Updating progress with first question:', firstQuestion.id);
    console.log('Progress ID to update:', progress.id);
    
    const { data, error } = await supabase
      .from('quiz_user_progress')
      .update({
        started_at: now,
        current_question_id: firstQuestion.id,
        path_taken: [],
        score: 0
      })
      .eq('id', progress.id)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating progress:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
    
    console.log("Flow content game started successfully:", data);
    
    // Đảm bảo trả về dữ liệu ở định dạng cả snake_case và camelCase
    const resultProgress = {
      id: data.id,
      user_id: data.user_id,
      userId: data.user_id,
      game_id: data.game_id,
      gameId: data.game_id,
      current_question_id: data.current_question_id,
      currentQuestionId: data.current_question_id,
      completed_questions: data.completed_questions || [],
      completedQuestions: data.completed_questions || [],
      score: data.score || 0,
      started_at: data.started_at,
      startedAt: data.started_at,
      completed_at: data.completed_at,
      completedAt: data.completed_at,
      path_taken: data.path_taken || [],
      pathTaken: data.path_taken || [],
    } as QuizUserProgress;
    
    console.log('Returning final progress object:', resultProgress);
    return resultProgress;
  } catch (error) {
    console.error('Error starting flow content game:', error);
    throw error;
  }
}

export async function makeFlowChoice(
  userId: string,
  gameId: string,
  currentQuestionId: string,
  choiceId: string
): Promise<{
  nextQuestion: QuizQuestion | null,
  isEndNode: boolean,
  effect: any,
  updatedProgress: QuizUserProgress | null
}> {
  try {
    console.log(`Making flow choice for user ${userId}, game ${gameId}, question ${currentQuestionId}, choice ${choiceId}`);
    
    // Get the current question
    const currentQuestion = await getQuestionById(currentQuestionId);
    if (!currentQuestion) {
      console.error('Current question not found:', currentQuestionId);
      throw new Error('Question not found');
    }
    
    console.log('Current question found:', currentQuestion.id);
    
    // Extract the choice and its effect from the question options - handle both formats
    let options: any = currentQuestion.options;
    
    console.log('Current question options before processing:', typeof options, options);
    
    // Xử lý options đa dạng
    if (typeof options === 'string') {
      try {
        options = JSON.parse(options);
        console.log('Options parsed from string:', options);
      } catch (e) {
        console.error('Failed to parse options string:', e);
      }
    }
    
    // Kiểm tra xem options có phải là cấu trúc { options: {...} } không
    if (typeof options === 'object' && options !== null && 'options' in options) {
      console.log('Options found in nested structure, extracting');
      options = options.options;
    }
    
    console.log('Processed options:', options);
    
    // Xử lý choices đa dạng
    let choices: any[] = [];
    if (options?.choices && Array.isArray(options.choices)) {
      choices = options.choices;
    } else if (Array.isArray(options)) {
      choices = options;
    } else if (typeof options === 'object' && options !== null) {
      // Thử tìm mảng lựa chọn trong object
      const possibleChoicesFields = ['choices', 'options', 'answers', 'items'];
      for (const field of possibleChoicesFields) {
        if (field in options && Array.isArray(options[field])) {
          choices = options[field];
          console.log(`Found choices in field: ${field}`);
          break;
        }
      }
    }
    
    console.log('Extracted choices:', choices);
    
    // Tìm kiếm lựa chọn được chọn
    const selectedChoice = choices.find((choice: any) => choice.id === choiceId);
    
    if (!selectedChoice) {
      console.error('Selected choice not found:', choiceId, 'Available choices:', choices);
      throw new Error('Choice not found');
    }
    
    console.log('Selected choice:', selectedChoice);
    
    // Check if this is an end node
    const isEndNode = options?.is_end_node || selectedChoice.is_end_node || false;
    console.log('Is end node:', isEndNode);
    
    // Get the next question ID from the selected choice
    const nextQuestionId = selectedChoice.next_question_id;
    console.log('Next question ID:', nextQuestionId);
    
    // Get the next question
    let nextQuestion = null;
    if (nextQuestionId && !isEndNode) {
      nextQuestion = await getQuestionById(nextQuestionId);
      console.log('Next question:', nextQuestion ? nextQuestion.id : 'Not found');
    }
    
    // Get current progress
    const currentProgress = await getUserProgress(userId, gameId);
    if (!currentProgress) {
      console.error('Progress not found for user:', userId, 'game:', gameId);
      throw new Error('No progress found');
    }
    
    console.log('Current progress:', currentProgress);
    
    // Create path record for this choice
    const pathRecord: FlowUserPathRecord = {
      question_id: currentQuestionId,
      choice_id: choiceId,
      timestamp: new Date().toISOString(),
      effects: selectedChoice.effect ? [{
        attribute: selectedChoice.effect.attribute || 'general',
        change: selectedChoice.effect.value || 0,
        description: selectedChoice.effect.description || ''
      }] : []
    };
    
    console.log('Created path record:', pathRecord);
    
    // Xử lý path_taken đa dạng, đảm bảo nó là một mảng
    let existingPathTaken: any[] = [];
    if (Array.isArray(currentProgress.path_taken)) {
      existingPathTaken = currentProgress.path_taken;
    } else if (Array.isArray(currentProgress.pathTaken)) {
      existingPathTaken = currentProgress.pathTaken;
    }
    
    console.log('Existing path taken:', existingPathTaken);
    
    // Update the path taken
    const pathTaken = [...existingPathTaken, pathRecord];
    console.log('New path taken:', pathTaken);
    
    // Xử lý completed_questions đa dạng, đảm bảo nó là một mảng
    let existingCompletedQuestions: string[] = [];
    if (Array.isArray(currentProgress.completed_questions)) {
      existingCompletedQuestions = currentProgress.completed_questions;
    } else if (Array.isArray(currentProgress.completedQuestions)) {
      existingCompletedQuestions = currentProgress.completedQuestions;
    }
    
    console.log('Existing completed questions:', existingCompletedQuestions);
    
    // Update the completed questions
    const completedQuestions = [...existingCompletedQuestions, currentQuestionId];
    console.log('New completed questions:', completedQuestions);
    
    // Calculate the new score based on the effect
    let newScore = currentProgress.score || 0;
    if (selectedChoice.effect && selectedChoice.effect.value) {
      newScore += selectedChoice.effect.value;
      console.log(`Updated score: ${newScore} (${selectedChoice.effect.value} added)`);
    }
    
    // Prepare the update
    const updateData = {
      completed_questions: completedQuestions,
      current_question_id: nextQuestionId || null,
      path_taken: pathTaken,
      score: newScore,
      // If this is an end node, mark as completed
      completed_at: isEndNode ? new Date().toISOString() : null
    };
    
    console.log('Update data for progress:', updateData);
    
    // Update the progress
    const { data, error } = await supabase
      .from('quiz_user_progress')
      .update(updateData)
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating flow progress:', error);
      throw error;
    }
    
    console.log('Progress updated successfully:', data);
    
    // Đảm bảo tương thích với cả camelCase và snake_case
    const updatedProgress: QuizUserProgress = {
      id: data.id,
      user_id: data.user_id,
      userId: data.user_id,
      game_id: data.game_id,
      gameId: data.game_id,
      current_question_id: data.current_question_id,
      currentQuestionId: data.current_question_id,
      completed_questions: data.completed_questions || [],
      completedQuestions: data.completed_questions || [],
      score: data.score || 0,
      correct_answers: data.correct_answers || 0, 
      started_at: data.started_at,
      startedAt: data.started_at,
      completed_at: data.completed_at,
      completedAt: data.completed_at,
      path_taken: data.path_taken || [],
      pathTaken: data.path_taken || []
    };
    
    console.log('Ready to return with next question and updated progress');
    
    return {
      nextQuestion,
      isEndNode,
      effect: selectedChoice.effect,
      updatedProgress
    };
  } catch (error) {
    console.error('Error in makeFlowChoice:', error);
    throw error;
  }
}

export async function getFlowGameResults(userId: string, gameId: string): Promise<FlowGameResults | null> {
  try {
    // Get the user progress
    const progress = await getUserProgress(userId, gameId);
    if (!progress || !progress.completedAt) {
      return null;
    }
    
    // Get the path taken
    const pathTaken = progress.path_taken || [];
    if (pathTaken.length === 0) {
      return null;
    }
    
    // Calculate attribute totals
    const attributeTotals: Record<string, number> = {};
    
    pathTaken.forEach(record => {
      if (!record.effects) return;
      
      record.effects.forEach(effect => {
        const attribute = effect.attribute || 'general';
        attributeTotals[attribute] = (attributeTotals[attribute] || 0) + effect.change;
      });
    });
    
    // Determine the dominant attribute (the one with highest positive value)
    let dominantAttribute = 'general';
    let highestValue = attributeTotals['general'] || 0;
    
    Object.entries(attributeTotals).forEach(([attr, value]) => {
      if (value > highestValue) {
        dominantAttribute = attr;
        highestValue = value;
      }
    });
    
    // Generate achievements
    const achievements: string[] = [];
    
    // Add achievement based on dominant attribute
    if (dominantAttribute === 'gioi' && (attributeTotals['gioi'] || 0) > 20) {
      achievements.push('Người thực hành giới luật');
    } else if (dominantAttribute === 'dinh' && (attributeTotals['dinh'] || 0) > 20) {
      achievements.push('Người chuyên thiền định');
    } else if (dominantAttribute === 'tue' && (attributeTotals['tue'] || 0) > 20) {
      achievements.push('Người trí tuệ');
    }
    
    // Add achievement based on balance
    const values = Object.values(attributeTotals).filter(val => val > 0);
    const average = values.reduce((sum, val) => sum + val, 0) / values.length;
    const isBalanced = values.every(val => Math.abs(val - average) < 10);
    
    if (isBalanced && values.length >= 3) {
      achievements.push('Người cân bằng');
    }
    
    // Determine path summary
    let pathSummary = 'Hành trình cân bằng';
    if (dominantAttribute === 'gioi') {
      pathSummary = 'Con đường Giới - Chú trọng giới luật';
    } else if (dominantAttribute === 'dinh') {
      pathSummary = 'Con đường Định - Chú trọng thiền định';
    } else if (dominantAttribute === 'tue') {
      pathSummary = 'Con đường Tuệ - Chú trọng trí tuệ';
    }
    
    // Generate conclusion
    let conclusion = {
      title: 'Tâm Bình An',
      description: 'Bạn đã hoàn thành hành trình tu tập với nhiều lựa chọn cân bằng.',
      image: '/images/balanced-path.jpg',
      share_text: 'Tôi đã hoàn thành Con Đường Tu Tập và đạt được trạng thái Tâm Bình An!'
    };
    
    if (dominantAttribute === 'gioi') {
      conclusion = {
        title: 'Tâm Thanh Tịnh',
        description: 'Bạn đã chọn con đường giới hạnh. Những lựa chọn của bạn thể hiện sự tự chủ và có kỷ luật cao. Hãy tiếp tục phát huy!',
        image: '/images/gioi-path.jpg',
        share_text: 'Tôi đã hoàn thành Con Đường Tu Tập và đạt được trạng thái Tâm Thanh Tịnh!'
      };
    } else if (dominantAttribute === 'dinh') {
      conclusion = {
        title: 'Tâm Định Tĩnh',
        description: 'Bạn đã chọn con đường thiền định. Bạn có khả năng làm chủ tâm mình và không dễ bị phiền não chi phối. Hãy tiếp tục duy trì sự tĩnh lặng nội tâm!',
        image: '/images/dinh-path.jpg',
        share_text: 'Tôi đã hoàn thành Con Đường Tu Tập và đạt được trạng thái Tâm Định Tĩnh!'
      };
    } else if (dominantAttribute === 'tue') {
      conclusion = {
        title: 'Tâm Trí Tuệ',
        description: 'Bạn đã chọn con đường trí tuệ. Bạn có khả năng nhìn nhận vấn đề một cách sáng suốt và đưa ra những quyết định khôn ngoan. Hãy tiếp tục phát triển trí tuệ của mình!',
        image: '/images/tue-path.jpg',
        share_text: 'Tôi đã hoàn thành Con Đường Tu Tập và đạt được trạng thái Tâm Trí Tuệ!'
      };
    }
    
    return {
      attribute_totals: attributeTotals,
      path_summary: pathSummary,
      achievements,
      conclusion
    };
  } catch (error) {
    console.error('Error in getFlowGameResults:', error);
    return null;
  }
}

// Function để lấy câu hỏi đầu tiên của game
export async function getFirstQuestionOfGame(gameId: string): Promise<QuizQuestion | null> {
  try {
    console.log(`Đang tìm câu hỏi đầu tiên cho game ${gameId}`);
    
    // Thử lấy câu hỏi đầu tiên theo thứ tự tạo (created_at ASC)
    let { data, error } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('game_id', gameId)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();
      
    if (error) {
      console.error('Error fetching first question by created_at:', error);
      
      // Fallback: Thử lấy câu hỏi đầu tiên theo bất kỳ tiêu chí nào
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('game_id', gameId)
        .limit(1);
        
      if (fallbackError || !fallbackData || fallbackData.length === 0) {
        console.error('Error fetching any question for this game:', fallbackError);
        
        // Fallback cuối cùng: Tạo câu hỏi mặc định cho game này
        console.log('Creating default question for game:', gameId);
        
        try {
          // Lấy thông tin game
          const { data: gameData, error: gameError } = await supabase
            .from('quiz_games')
            .select('*')
            .eq('id', gameId)
            .single();
            
          if (gameError) {
            console.error('Error fetching game info:', gameError);
            return null;
          }
          
          console.log('Game info retrieved:', gameData);
          
          // Tạo một câu hỏi mặc định phù hợp với cấu trúc bảng quiz_questions
          const defaultQuestion = {
            id: crypto.randomUUID(),
            game_id: gameId,
            question_text: 'Bắt đầu hành trình của bạn',
            options: JSON.stringify({
              choices: [
                {
                  id: 'choice-1',
                  text: 'Tiếp tục',
                  next_question_id: null,
                  is_end_node: true,
                  effect: {
                    type: 'positive',
                    value: 10,
                    attribute: 'general',
                    description: 'Bắt đầu hành trình'
                  }
                }
              ],
              is_end_node: false
            }),
            correct_answer: null,
            explanation: 'Đây là câu hỏi mặc định cho flow content',
            difficulty: 1,
            points: 10,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          console.log('Default question prepared:', defaultQuestion);
          
          // Lưu câu hỏi vào database
          const { data: insertedQuestion, error: insertError } = await supabase
            .from('quiz_questions')
            .insert(defaultQuestion)
            .select()
            .single();
            
          if (insertError) {
            console.error('Error creating default question:', insertError);
            console.error('Error details:', JSON.stringify(insertError, null, 2));
            return null;
          }
          
          console.log('Created default question for flow content:', insertedQuestion);
          data = insertedQuestion;
        } catch (e) {
          console.error('Error in default question creation:', e);
          return null;
        }
      } else {
        // Sử dụng câu hỏi đầu tiên từ kết quả fallback
        data = fallbackData[0];
        console.log('Found fallback question for flow content game:', data.id);
      }
    } else {
      console.log('Found first question by created_at:', data.id);
    }
    
    // Chuyển đổi câu hỏi từ DB sang định dạng QuizQuestion
    const formattedQuestion = {
      ...data,
      id: data.id,
      gameId: data.game_id,
      questionText: data.question_text,
      correctAnswer: data.correct_answer,
      mediaType: data.media_type,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
      updatedAt: data.updated_at ? new Date(data.updated_at) : new Date()
    } as QuizQuestion;
    
    console.log('Returning question:', formattedQuestion.id);
    return formattedQuestion;
  } catch (error) {
    console.error('Error in getFirstQuestionOfGame:', error);
    return null;
  }
}

// Function để tạo user progress mới
export async function createUserProgress(
  userId: string, 
  gameId: string, 
  firstQuestionId: string
): Promise<QuizUserProgress> {
  try {
    console.log(`Creating new user progress - User: ${userId}, Game: ${gameId}, First Question: ${firstQuestionId}`);
    
    const progressData = {
      user_id: userId,
      game_id: gameId,
      score: 0,
      completed_questions: [],
      current_question_id: firstQuestionId,
      path_taken: [],
      correct_answers: 0, // Thêm trường correct_answers
      started_at: new Date().toISOString(),
      completed_at: null
    };
    
    console.log('Progress data to insert:', progressData);
    
    const { data, error } = await supabase
      .from('quiz_user_progress')
      .insert(progressData)
      .select()
      .single();
      
    if (error) {
      console.error('Error creating user progress:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
    
    console.log('Progress created successfully:', data);
    
    // Chuyển đổi dữ liệu thành định dạng QuizUserProgress
    const formattedProgress = {
      id: data.id,
      userId: data.user_id,
      user_id: data.user_id,
      gameId: data.game_id,
      game_id: data.game_id,
      currentQuestionId: data.current_question_id,
      current_question_id: data.current_question_id,
      completedQuestions: data.completed_questions || [],
      completed_questions: data.completed_questions || [],
      score: data.score || 0,
      correct_answers: data.correct_answers || 0,
      startedAt: data.started_at,
      started_at: data.started_at,
      completedAt: data.completed_at,
      completed_at: data.completed_at,
      pathTaken: data.path_taken || [],
      path_taken: data.path_taken || [],
    } as QuizUserProgress;
    
    console.log('Returning formatted progress:', formattedProgress);
    return formattedProgress;
  } catch (error) {
    console.error('Error creating user progress:', error);
    throw error;
  }
} 